import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { preloadPoolScene } from '../poolside-assets.js';

// Contrat du rig partagé : interdit. La seule lumière de la scène sort de
// la piscine : elle colore l'eau et projette ses caustiques sur le décor.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'poolside',
  family: 'photographic',
  danceability: 0.70,
  energyLevel: 0.66,
  bpmMin: 100,
  bpmMax: 128,
  movement: 0.55,
  tonality: 0.80,
  prefers: { brightness: [0.40, 1.0] },
  enabled: true
};

// Poolside : une pool party de nuit à partir d'une photo de piscine éteinte
// (assets/pool.jpeg, construite par tools/build-scenes.mjs : variantes,
// masques, profondeur). L'eau devient lumineuse par en dessous, ses
// caustiques dansent sur le mur de la villa et les palmiers, chaque temps
// envoie une onde sur la surface.
//
// LA GÉOMÉTRIE. Le manifeste porte le point de fuite et les quatre coins du
// quadrilatère de l'eau. Déprojetés sur le sol (caméra à hc = 1 - vy au-dessus,
// bas du cadre à la profondeur 1, les unités de depth-scene.py), ils font un
// rectangle x +-1,1 sur z 1,08..6,98 : c'est le plan de l'eau, et le champ de
// nappe vit dans ses coordonnées (u en travers, v en longueur). Un pixel
// d'eau se déprojette sur ce plan ; un pixel du décor prend sa position 3D
// par la carte de profondeur, et SA PROJECTION VERTICALE sur le plan de l'eau
// dit quelle caustique l'éclaire, atténuée par la hauteur au-dessus de l'eau
// et la distance hors du bassin. Le mur du fond est juste derrière le bord
// lointain : il reçoit le réseau de filaments en entier.
//
// LA NAPPE est simulée sur le CPU (équation des ondes, 128 x 256 cellules,
// parois réfléchissantes) et envoyée en RGBA8 filtrable - le Pi ne filtre
// pas le flottant : R hauteur, GB pente, A LAPLACIEN. Le laplacien est la
// courbure de la surface : c'est lui qui focalise la lumière, et la
// caustique en est une fonction directe, sans second champ.
//
// Le fond du bassin est un carrelage procédural réfracté par la pente ; les
// quatre projecteurs immergés éclairent par en dessous ; une guirlande entre
// deux palmiers et quelques lucioles de bokeh font l'ambiance.

const TIER = {
  0: { grid: [64, 128], refraction: false, decor: false, decorLights: 2, photo: 1600, fireflies: 0 },
  1: { grid: [64, 128], refraction: false, decor: true, decorLights: 2, photo: 1600, fireflies: 3 },
  2: { grid: [128, 256], refraction: true, decor: true, decorLights: 4, photo: 2560, fireflies: 5 }
};
const RIPPLE_C = 0.28;                // vitesse (stabilité : < 0.5)
const RIPPLE_DAMPING = 0.991;
const DROP_AMP = [0.8, 2.2];          // par bands.low
const DROP_RADIUS = 3.0;              // cellules
const DIVE_AMP = 4.5;
const DIVE_RADIUS = 6.0;              // cellules
const DIVE_S = 2.2;                   // bulles et éclaboussure
const BAR_PULSE = 1.3;
const FLASH_WHITE_S = 0.35;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const WATER_BASE = [0.10 * 1.3, 0.75 * 1.3, 0.85 * 1.3];   // turquoise lumineux, x1,3
const PALETTE_WATER = 0.30;                                  // la teinte seulement, luminance gardée
const PALM_SWAY_PX = 3;
const SAFE_FLOOR = 0.55;
const W_MAX_DEFAULT = 1.25;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

function buildFragmentShader(L) {
  return `
  precision mediump float;
  uniform sampler2D uMap;
  uniform sampler2D uMasks;
  uniform sampler2D uDepth;
  uniform sampler2D uRipple;
  uniform float uWMax;
  uniform highp vec4 uProj;     // vx, vy, aspect, hc
  uniform vec2 uFit;
  uniform highp vec4 uPool;     // x0, z0, 1/largeur, 1/longueur
  uniform vec3 uWater;
  uniform vec2 uLightPos[4];    // en coordonnées du bassin
  uniform vec3 uLightCol[4];
  uniform float uLightGain;
  uniform float uCaustGain;
  uniform float uDecorOn;
  uniform float uMaskView;
  uniform float uSolo;
  uniform float uTime;
  uniform float uSway;          // en fraction de la largeur de l'image
  uniform vec4 uStatic[2];      // rectangles (u0 v0 u1 v1) qui ne bougent pas
  uniform vec4 uDive;           // u, v, âge, actif
  uniform float uBulb;
  uniform vec4 uGarland;        // from.xy, to.xy (image)
  uniform float uSag;
  uniform vec3 uFirefly[5];     // x, y (image), rayon
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  vec2 imageOfNdc(vec2 ndc) { return vec2(0.5 + ndc.x * 0.5 * uFit.x, 0.5 - ndc.y * 0.5 * uFit.y); }
  highp vec3 rayOf(highp vec2 img) { return vec3((img.x - uProj.x) * uProj.z, uProj.y - img.y, 1.0); }
  vec2 tc(vec2 img) { return vec2(img.x, 1.0 - img.y); }
  highp float depthAt(vec2 img) {
    vec2 rg = texture2D(uDepth, tc(img)).rg;
    return (256.0 * rg.x + rg.y) * (uWMax / 257.0);
  }
  float hash21(vec2 p) { vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  // la caustique : une fonction du laplacien (la courbure focalise)
  float caustic(float lap) {
    float c = 0.15 / (abs(1.0 - 6.0 * lap) + 0.15);
    return max(c - 0.13, 0.0) * 3.0;
  }
  // un projecteur : un halo elliptique dans le plan de l'eau, un coeur au-dessus du seuil de bloom
  vec3 halo(int i, vec2 p) {
    vec2 d = (p - uLightPos[i]) * vec2(1.0, 2.5);
    float d2 = dot(d, d);
    return uLightCol[i] * (0.75 / (1.0 + d2 * 12.0) + 1.3 * exp(-d2 * 90.0));
  }
  vec3 lightsAt(vec2 p) {
    vec3 l = vec3(0.0);
    for (int i = 0; i < 4; i++) l += halo(i, p);
    return l * uLightGain;
  }
  bool inRect(vec2 img, vec4 r) { return img.x > r.x && img.x < r.z && img.y > r.y && img.y < r.w; }

  void main() {
    highp vec2 img = imageOfNdc(vUv * 2.0 - 1.0);
    vec3 masks = texture2D(uMasks, tc(img)).rgb;
    float water = smoothstep(0.35, 0.65, masks.r);   // un bord de 2 px, adouci au build
    float wall = masks.g, palms = masks.b;
    vec3 photo = texture2D(uMap, tc(img)).rgb;
    float luma = dot(photo, vec3(0.299, 0.587, 0.114));
    float deck = (1.0 - water) * (1.0 - wall) * (1.0 - smoothstep(0.3, 0.7, palms)) * step(uProj.y, img.y) * smoothstep(0.30, 0.45, luma);
    if (uMaskView > 0.5) {
      gl_FragColor = vec4(photo * 0.3 + vec3(water, wall, palms) * 0.6 + vec3(deck) * vec3(0.6, 0.6, 0.0), 1.0);
      return;
    }
    vec3 col = photo * 0.85;
    highp vec3 ray = rayOf(img);
    vec3 waterCol = vec3(0.0);

    if (water > 0.001) {
      // --- L'EAU : le pixel déprojeté sur le plan de l'eau, dans le quad seulement ---
      highp float z = uProj.w / max(img.y - uProj.y, 0.002);
      highp vec2 fp = vec2(ray.x * z, z);
      vec2 p = clamp(vec2((fp.x - uPool.x) * uPool.z, (fp.y - uPool.y) * uPool.w), 0.0, 1.0);
      vec4 r = texture2D(uRipple, p);
      vec2 slope = (r.gb - 0.5) * 2.0;
      float lap = (r.a - 0.5) * 2.0;
      vec3 lights = lightsAt(p);
      // la profondeur : plus clair vers le premier plan, plus sombre et plus saturé vers le fond
      float toBack = p.y;
      vec3 base = uWater * mix(1.15, 0.55, toBack);
      base = mix(base, base * base / max(uWater, vec3(0.05)), toBack * 0.5);
      // le carrelage vu A TRAVERS l'eau : contraste 30 %, joints fins et clairs, réfractés
      vec2 tp = p * vec2(14.0, 35.0)${L.refraction ? ' + slope * 1.8' : ''};
      vec2 g = abs(fract(tp) - 0.5);
      float grout = 1.0 - smoothstep(0.455, 0.49, max(g.x, g.y));
      float tile = 0.85 + 0.30 * grout + 0.06 * (hash21(floor(tp)) - 0.5);
      float caust = caustic(lap);
      // le corps de l'eau domine, le carrelage transparaît à 30 % de contraste
      vec3 under = base * (0.22 + lights * 0.28) * (0.7 + 0.3 * tile) + lights * caust * 0.55 + base * caust * 0.35;
      // Fresnel : le ciel et les palmiers de la photo sur le tiers lointain
      float fres = 0.06 + 0.55 * smoothstep(0.5, 1.0, toBack);
      vec3 refl = photo * 1.5 + lights * 0.06;
      waterCol = mix(under, refl, fres);
      // le plongeon : éclaboussure et bulles au point d'entrée
      if (uDive.w > 0.5) {
        float age = uDive.z;
        vec2 dd = (p - uDive.xy) * vec2(1.0, 2.5);
        float splash = exp(-dot(dd, dd) * 60.0) * max(0.0, 1.0 - age * 2.5);
        float bub = 0.0;
        for (int i = 0; i < 6; i++) {
          float fi = float(i);
          vec2 off = vec2(hash21(vec2(fi, 1.0)) - 0.5, hash21(vec2(fi, 2.0)) - 0.5) * 0.12;
          vec2 bp = uDive.xy + off + vec2(0.0, -age * 0.06 * (0.5 + hash21(vec2(fi, 3.0))));
          float bd = length((p - bp) * vec2(1.0, 2.5));
          bub += smoothstep(0.012, 0.004, bd) * max(0.0, 1.0 - age / ${DIVE_S.toFixed(1)});
        }
        waterCol += vec3(0.9, 1.0, 1.0) * (splash * 1.2 + bub * 0.6);
      }
      col = mix(col, waterCol, water);
    }
    float decorMask = max(wall, max(smoothstep(0.3, 0.7, palms), deck)) * (1.0 - water);
    if (uDecorOn > 0.5 && decorMask > 0.02) {
      // --- LE DÉCOR : la position 3D par la profondeur ; chaque projecteur immergé
      // l'éclaire par le point de la surface que traverse SON rayon (la droite
      // du projecteur, à la profondeur dp sous l'eau, vers le point) : un pixel
      // du mur lit la caustique qui lui fait face, décalée avec sa hauteur, et
      // le motif s'y agrandit de (dp + h) / dp - deux à quatre fois celui du fond.
      highp float z = 1.0 / max(depthAt(img), 0.02);
      highp vec3 P = ray * z;
      float above = max(P.y + uProj.w, 0.0);
      vec2 pxz = vec2((P.x - uPool.x) * uPool.z, (P.z - uPool.y) * uPool.w);   // en coordonnées du bassin
      vec2 outUnits = max(abs(pxz - 0.5) - 0.5, 0.0) / vec2(uPool.z, uPool.w);
      float outside = length(outUnits);
      const float dp = 0.45;
      vec3 light = vec3(0.0);
      for (int i = 0; i < ${L.decorLights}; i++) {
        vec2 q = uLightPos[i];
        vec2 ps = q + (pxz - q) * (dp / (dp + above));
        float inside = smoothstep(-0.02, 0.03, ps.x) * smoothstep(1.02, 0.97, ps.x) * smoothstep(-0.02, 0.03, ps.y) * smoothstep(1.02, 0.97, ps.y);
        vec4 r = texture2D(uRipple, clamp(ps, 0.0, 1.0));
        float caust = caustic((r.a - 0.5) * 2.0);
        vec2 dq = (pxz - q) / vec2(uPool.z, uPool.w);   // en unités sol
        float fall = 1.0 / (1.0 + dot(dq, dq) * 0.35);
        // dans la couleur de l'eau, teintée par le projecteur
        light += mix(uWater / max(max(uWater.r, uWater.g), uWater.b), uLightCol[i], 0.45) * (0.22 + caust * 1.7) * fall * inside;
      }
      float heightAtt = 1.0 / (1.0 + above * 0.9);
      // la terrasse : une bande de 1,5 m (0,65 unité) depuis le bord, atténuée
      float band = exp(-outside / 0.30);
      float weight = wall * 1.25 + smoothstep(0.3, 0.7, palms) * 0.55 * min(1.0, img.y / 0.35) + deck * 0.55 * band;
      light *= heightAtt * uCaustGain * weight * uLightGain;
      // les palmes au vent : la photo lue décalée, pondérée par le masque adouci,
      // jamais hors du masque (sinon le ciel ou la terrasse), jamais dans les
      // zones statiques (bâtiment, transats), jamais sous les troncs
      float swayW = palms * smoothstep(0.52, 0.40, img.y);
      if (inRect(img, uStatic[0]) || inRect(img, uStatic[1])) swayW = 0.0;
      vec3 ph = photo;
      if (swayW > 0.002) {
        vec2 swayImg = img + vec2(sin(uTime * 1.3 + img.y * 9.0) * uSway, 0.0);
        float palmsThere = texture2D(uMasks, tc(swayImg)).b;
        ph = mix(photo, texture2D(uMap, tc(swayImg)).rgb, swayW * step(0.5, palmsThere));
      }
      // la terrasse hors lumière : plafonnée à 0,12
      ph = mix(ph, ph * min(1.0, 0.12 / max(luma, 0.001)), deck);
      vec3 lit = ph * (0.85 + light * 2.0) + light * 0.3;
      col = mix(col, lit, decorMask);
    }

    // --- la guirlande : un câble en chaînette entre deux troncs, les ampoules pendues dessous ---
    // (évaluée dans sa seule boîte englobante : 42 distances par pixel sur la
    // moitié du cadre coûtaient 30 ms au tier 1 sur le Pi)
    if (img.x > min(uGarland.x, uGarland.z) - 0.015 && img.x < max(uGarland.x, uGarland.z) + 0.015
        && img.y > min(uGarland.y, uGarland.w) - 0.015 && img.y < max(uGarland.y, uGarland.w) + uSag + 0.03) {
      float cable = 1e9, best = 1e9;
      for (int i = 0; i < 28; i++) {
        float t = (float(i) + 0.5) / 28.0;
        vec2 b = mix(uGarland.xy, uGarland.zw, t) + vec2(0.0, uSag * 4.0 * t * (1.0 - t));
        cable = min(cable, length((img - b) * vec2(uProj.z, 1.0)));
      }
      for (int i = 0; i < 14; i++) {
        float t = (float(i) + 0.5) / 14.0;
        vec2 b = mix(uGarland.xy, uGarland.zw, t) + vec2(0.0, uSag * 4.0 * t * (1.0 - t) + 0.012);
        best = min(best, length((img - b) * vec2(uProj.z, 1.0)));
      }
      float wire = 1.0 - smoothstep(0.0, 0.0022, cable);
      col = mix(col, vec3(0.03, 0.03, 0.035), wire * 0.8);
      float bulb = exp(-best * best * 60000.0) * 1.4 + exp(-best * best * 3000.0) * 0.35;
      col += vec3(1.0, 0.72, 0.38) * bulb * uBulb;
    }
    // --- les lucioles de bokeh, très floues, au premier plan ---
    for (int i = 0; i < ${L.fireflies}; i++) {
      float d = length((img - uFirefly[i].xy) * vec2(uProj.z, 1.0));
      col += vec3(1.0, 0.85, 0.5) * smoothstep(uFirefly[i].z, uFirefly[i].z * 0.3, d) * 0.22;
    }
    if (uSolo > 0.5) col = waterCol * water;   // ?poolwater=solo : l'eau seule
    gl_FragColor = vec4(col * safeAtt(vUv), 1.0);
  }
  `;
}

const MATERIALS = new Map();
const _size = new THREE.Vector2();

export class PoolsideVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    this._maskView = urlParam('poolmask') === '1';
    this._debugDive = urlParam('pooldive') === '1';
    this._caustOff = urlParam('poolcaust') === '0';
    this._solo = urlParam('poolwater') === 'solo';
    this._postProcessing = { bloomStrength: 0.4, bloomThreshold: 0.85 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout;
    this._time = 0; this._paused = false; this._ready = false;
    this._seed = 5;
    // --- la nappe ---
    const [gw, gh] = L.grid;
    this._gw = gw; this._gh = gh;
    this._h0 = new Float32Array(gw * gh); this._h1 = new Float32Array(gw * gh);
    this._rippleData = new Uint8Array(gw * gh * 4);
    for (let i = 0; i < gw * gh; i++) { this._rippleData[i * 4] = 128; this._rippleData[i * 4 + 1] = 128; this._rippleData[i * 4 + 2] = 128; this._rippleData[i * 4 + 3] = 128; }
    this._rippleTex = new THREE.DataTexture(this._rippleData, gw, gh, THREE.RGBAFormat, THREE.UnsignedByteType);
    this._rippleTex.minFilter = THREE.LinearFilter; this._rippleTex.magFilter = THREE.LinearFilter;
    this._rippleTex.wrapS = this._rippleTex.wrapT = THREE.ClampToEdgeWrapping;
    this._rippleTex.generateMipmaps = false; this._rippleTex.needsUpdate = true;

    const key = `${this._tier}:${L.refraction ? 1 : 0}:${L.fireflies}:${L.decorLights}`;
    let mat = MATERIALS.get(key);
    if (!mat) {
      mat = new THREE.ShaderMaterial({
        uniforms: {
          uMap: { value: null }, uMasks: { value: null }, uDepth: { value: null }, uRipple: { value: null },
          uWMax: { value: W_MAX_DEFAULT },
          uProj: { value: new THREE.Vector4(0.5, 0.45, 1376 / 768, 0.55) }, uFit: { value: new THREE.Vector2(1, 1) },
          uPool: { value: new THREE.Vector4(-1.1, 1.08, 1 / 2.2, 1 / 5.9) },
          uWater: { value: new THREE.Color(...WATER_BASE) },
          uLightPos: { value: [new THREE.Vector2(0, 0.28), new THREE.Vector2(1, 0.28), new THREE.Vector2(0, 0.72), new THREE.Vector2(1, 0.72)] },
          uLightCol: { value: [new THREE.Color(1, 1, 1), new THREE.Color(1, 1, 1), new THREE.Color(1, 1, 1), new THREE.Color(1, 1, 1)] },
          uLightGain: { value: 1 }, uCaustGain: { value: 1 }, uDecorOn: { value: 1 }, uMaskView: { value: 0 }, uSolo: { value: 0 },
          uStatic: { value: [new THREE.Vector4(-1, -1, -1, -1), new THREE.Vector4(-1, -1, -1, -1)] },
          uTime: { value: 0 }, uSway: { value: 0 },
          uDive: { value: new THREE.Vector4(0.5, 0.5, 0, 0) },
          uBulb: { value: 0.6 }, uGarland: { value: new THREE.Vector4(0.35, 0.27, 0.63, 0.30) }, uSag: { value: 0.045 },
          uFirefly: { value: [0, 1, 2, 3, 4].map(() => new THREE.Vector3(-1, -1, 0.01)) },
          uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
          uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
        },
        vertexShader: quadVertexShader, fragmentShader: buildFragmentShader(L), depthTest: false, depthWrite: false
      });
      MATERIALS.set(key, mat);
    }
    this._mat = mat;
    this._u = mat.uniforms;
    this._u.uRipple.value = this._rippleTex;
    this._u.uMaskView.value = this._maskView ? 1 : 0;
    this._u.uSolo.value = this._solo ? 1 : 0;
    this._u.uDecorOn.value = (L.decor && !this._caustOff) ? 1 : 0;
    this._u.uDive.value.set(0.5, 0.5, 0, 0);
    this._u.uMap.value = null;

    this._photo = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._photo.frustumCulled = false;
    this._photo.renderOrder = -1000;
    this._photo.visible = false;
    const self = this;
    this._photo.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._fit(_size.x, _size.y); };
    scene.add(this._photo);

    // l'état musical
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._secondsBeat = 0.5;
    this._low = 0.5; this._mid = 0.3; this._high = 0.4;
    this._barAge = 10; this._dive = null; this._whiteUntil = -1; this._dropUntil = -1;
    this._bulb = 0.6; this._palShift = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined; this._vivid = null;
    this._fireflies = [];
    for (let i = 0; i < L.fireflies; i++) this._fireflies.push({ x: this._random(), y: 0.45 + 0.5 * this._random(), r: 0.012 + 0.02 * this._random(), vx: (this._random() - 0.5) * 0.01, vy: (this._random() - 0.5) * 0.006, ph: this._random() * 6.28 });
    this._load();
  }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  _load() {
    const self = this, L = this._layout;
    preloadPoolScene().then(function (m) {
      if (!m || !self._scene) return null;
      self._meta = m;
      const A = m.sourceSize[0] / m.sourceSize[1];
      self._aspectPhoto = A;
      const vx = m.vanish.x, vy = m.vanish.y, hc = 1 - vy;
      self._u.uProj.value.set(vx, vy, A, hc);
      // le bassin : les coins déprojetés sur le plan de l'eau
      let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (const [u, v] of m.water.corners) {
        const z = hc / Math.max(v - vy, 0.002), x = (u - vx) * A * z;
        x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z);
      }
      self._pool = { x0, x1, z0, z1 };
      self._u.uPool.value.set(x0, z0, 1 / (x1 - x0), 1 / (z1 - z0));
      if (m.garland) { self._u.uGarland.value.set(m.garland.from[0], m.garland.from[1], m.garland.to[0], m.garland.to[1]); self._u.uSag.value = m.garland.sag || 0.045; }
      if (m.static) for (let i = 0; i < 2 && i < m.static.length; i++) self._u.uStatic.value[i].set(...m.static[i]);
      const want = L.photo;
      const photo = (m.variants || []).filter((v) => !v.role).sort((a, b) => Math.abs(a.width - want) - Math.abs(b.width - want))[0];
      const masks = (m.variants || []).find((v) => v.role === 'masks');
      const depth = (m.variants || []).find((v) => v.role === 'depth');
      self._wMax = (depth && depth.wMax) || W_MAX_DEFAULT;
      const load = (f) => new Promise((res, rej) => { new THREE.TextureLoader().load('textures/' + f, res, undefined, rej); });
      return Promise.all([load(photo.file), load(masks.file), depth ? load(depth.file).catch(() => null) : Promise.resolve(null)]);
    }).then(function (t) {
      if (!t || !self._scene) return;
      const [tex, mk, dep] = t;
      tex.colorSpace = THREE.SRGBColorSpace; tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
      tex.minFilter = THREE.LinearMipmapLinearFilter; tex.anisotropy = 1;
      mk.colorSpace = THREE.NoColorSpace; mk.wrapS = mk.wrapT = THREE.ClampToEdgeWrapping; mk.minFilter = THREE.LinearFilter; mk.magFilter = THREE.LinearFilter; mk.generateMipmaps = false;
      if (dep) { dep.colorSpace = THREE.NoColorSpace; dep.wrapS = dep.wrapT = THREE.ClampToEdgeWrapping; dep.minFilter = THREE.LinearFilter; dep.magFilter = THREE.LinearFilter; dep.generateMipmaps = false; }
      self._textures = [tex, mk, dep].filter(Boolean);
      self._u.uMap.value = tex; self._u.uMasks.value = mk; self._u.uDepth.value = dep; self._u.uWMax.value = self._wMax;
      if (!dep) self._u.uDecorOn.value = 0;    // sans profondeur, pas de décor éclairé
      self._photo.visible = true;
      self._ready = true;
      if (self._debugDive) self._startDive(0.5, 0.5);
    }).catch(function (err) {
      if (self._scene) console.warn('[poolside] scène indisponible (' + (err && err.message) + ') : lancer tools/build-scenes.mjs');
    });
  }

  _fit(wpx, hpx) {
    const af = wpx / Math.max(1, hpx), ap = this._aspectPhoto || 1376 / 768;
    const fit = this._u.uFit.value;
    if (af >= ap) fit.set(1, ap / af); else fit.set(af / ap, 1);
    this._u.uSway.value = PALM_SWAY_PX * (0.3 + 0.7 * this._mid) / Math.max(1, wpx) * fit.x;
  }

  // --- la palette : l'eau, les projecteurs ---------------------------------------------
  // La teinte de la palette, la luminance de la base : jamais un rouge sombre.
  // Une couleur de palette trop sombre (l < 0,4) est remontée avant mélange.
  _hued(base, src, amount) {
    const hs = { h: 0, s: 0, l: 0 }, hb = { h: 0, s: 0, l: 0 };
    src.getHSL(hs); base.getHSL(hb);
    if (hs.l < 0.4) hs.l = 0.5;
    // la TEINTE tourne (par le plus court chemin), saturation et luminance
    // restent : un lerp RGB entre cyan et rose donne un gris
    let dh = hs.h - hb.h; if (dh > 0.5) dh -= 1; if (dh < -0.5) dh += 1;
    // ... et de 25 degrés au plus : l'eau reste dans la famille du turquoise
    const rot = Math.max(-0.07, Math.min(0.07, dh * amount));
    base.setHSL((hb.h + rot + 1) % 1, hb.s, hb.l);
  }
  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0x2ee6d6, 0xff5fa2, 0xffc93d, 0x7a5cff];
    this._vivid = v.map((c) => new THREE.Color(c));
    this._paintLights();
  }
  _paintLights() {
    const U = this._u, v = this._vivid || [new THREE.Color(0.2, 0.9, 0.85)];
    U.uWater.value.setRGB(...WATER_BASE);
    this._hued(U.uWater.value, v[(this._palShift) % v.length], PALETTE_WATER);
    for (let i = 0; i < 4; i++) {
      const c = v[(i + this._palShift) % v.length];
      const hsl = { h: 0, s: 0, l: 0 }; c.getHSL(hsl);
      U.uLightCol.value[i].setHSL(hsl.h, 0.85, 0.62);
    }
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  // --- la nappe -------------------------------------------------------------------------
  _drop(u, v, amp, radius) {
    const gw = this._gw, gh = this._gh, h = this._h1;
    const cx = Math.round(u * (gw - 1)), cy = Math.round(v * (gh - 1));
    const R = Math.ceil(radius);
    for (let y = Math.max(1, cy - R); y <= Math.min(gh - 2, cy + R); y++) {
      for (let x = Math.max(1, cx - R); x <= Math.min(gw - 2, cx + R); x++) {
        const d2 = (x - cx) * (x - cx) + (y - cy) * (y - cy);
        h[y * gw + x] -= amp * Math.exp(-d2 / (radius * radius * 0.5));
      }
    }
  }
  _startDive(u, v) {
    this._drop(u, v, DIVE_AMP, DIVE_RADIUS);
    this._dive = { u, v, t: 0 };
    // un projecteur change de couleur
    if (this._vivid) {
      const i = Math.floor(this._random() * 4);
      const c = this._vivid[Math.floor(this._random() * this._vivid.length)];
      const hsl = { h: 0, s: 0, l: 0 }; c.getHSL(hsl);
      this._u.uLightCol.value[i].setHSL(hsl.h, 0.85, 0.62);
    }
  }
  _stepRipple(dt) {
    const gw = this._gw, gh = this._gh, h0 = this._h0, h1 = this._h1;
    const c = RIPPLE_C;
    // parois réfléchissantes : les bords copient l'intérieur
    for (let x = 0; x < gw; x++) { h1[x] = h1[gw + x]; h1[(gh - 1) * gw + x] = h1[(gh - 2) * gw + x]; }
    for (let y = 0; y < gh; y++) { h1[y * gw] = h1[y * gw + 1]; h1[y * gw + gw - 1] = h1[y * gw + gw - 2]; }
    for (let y = 1; y < gh - 1; y++) {
      for (let x = 1; x < gw - 1; x++) {
        const i = y * gw + x;
        const lap = h1[i - 1] + h1[i + 1] + h1[i - gw] + h1[i + gw] - 4 * h1[i];
        h0[i] = (2 * h1[i] - h0[i] + c * lap) * RIPPLE_DAMPING;
      }
    }
    this._h0 = h1; this._h1 = h0;
    const h = h0, d = this._rippleData;
    for (let y = 0; y < gh; y++) {
      for (let x = 0; x < gw; x++) {
        const i = y * gw + x;
        const xl = x > 0 ? h[i - 1] : h[i], xr = x < gw - 1 ? h[i + 1] : h[i];
        const yd = y > 0 ? h[i - gw] : h[i], yu = y < gh - 1 ? h[i + gw] : h[i];
        const lap = xl + xr + yd + yu - 4 * h[i];
        d[i * 4] = Math.max(0, Math.min(255, Math.round(128 + h[i] * 60)));
        d[i * 4 + 1] = Math.max(0, Math.min(255, Math.round(128 + (xr - xl) * 150)));
        d[i * 4 + 2] = Math.max(0, Math.min(255, Math.round(128 + (yu - yd) * 150)));
        d[i * 4 + 3] = Math.max(0, Math.min(255, Math.round(128 + lap * 500)));
      }
    }
    this._rippleTex.needsUpdate = true;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low = clamp01(bands.low); this._mid = clamp01(bands.mid); this._high = clamp01(bands.high); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const barS = 4 * this._secondsBeat;

    let beat = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } }
    let bar = false;
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
    else if (beat && (this._beats + 1) % 4 === 0) bar = true;
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;

    // DROP : projecteurs blancs puis vifs, trois plongeons, caustiques pleines
    if (this._tickDrops(profile)) {
      this._dropUntil = this._time + barS;
      this._whiteUntil = this._time + FLASH_WHITE_S;
      for (let i = 0; i < 3; i++) this._startDive(0.2 + 0.6 * this._random(), 0.2 + 0.6 * this._random());
      this._palShift++;
      this._paintLights();
    }
    const inDrop = this._time < this._dropUntil;
    // TEMPS : une goutte
    if (beat) {
      this._drop(0.08 + 0.84 * this._random(), 0.05 + 0.9 * this._random(), lerp(DROP_AMP[0], DROP_AMP[1], this._low), DROP_RADIUS);
      this._bulb = 1.0;
    }
    // MESURE : les projecteurs pulsent à 1,3 ; la couleur glisse d'un cran
    if (bar) { this._barAge = 0; this._palShift++; this._paintLights(); }
    this._barAge += dt;
    const pulse = Math.exp(-this._barAge / 0.18);
    // PHRASE : un plongeon
    if (phrase) this._startDive(0.15 + 0.7 * this._random(), 0.2 + 0.6 * this._random());
    if (this._dive) { this._dive.t += dt; if (this._dive.t > DIVE_S) this._dive = null; }

    this._stepRipple(dt);
    const U = this._u;
    U.uTime.value = this._time;
    U.uLightGain.value = (1 + (BAR_PULSE - 1) * pulse) * (inDrop ? 1.25 : 1);
    U.uCaustGain.value = inDrop ? 1.6 : 0.6 + 0.8 * this._high;
    const white = this._time < this._whiteUntil;
    if (white) for (let i = 0; i < 4; i++) U.uLightCol.value[i].setRGB(1, 1, 1);
    else if (this._wasWhite) this._paintLights();
    this._wasWhite = white;
    if (this._dive) U.uDive.value.set(this._dive.u, this._dive.v, this._dive.t, 1); else U.uDive.value.w = 0;
    // la guirlande : l'inertie d'un filament
    this._bulb += (0.55 - this._bulb) * (1 - Math.exp(-dt / 0.25));
    U.uBulb.value = this._bulb;
    // les lucioles
    for (let i = 0; i < this._fireflies.length; i++) {
      const f = this._fireflies[i];
      f.x += (f.vx + Math.sin(this._time * 0.7 + f.ph) * 0.004) * dt * 10; f.y += (f.vy + Math.cos(this._time * 0.5 + f.ph) * 0.003) * dt * 10;
      if (f.x < -0.05) f.x = 1.05; if (f.x > 1.05) f.x = -0.05; if (f.y < 0.3) f.y = 0.95; if (f.y > 0.98) f.y = 0.3;
      U.uFirefly.value[i].set(f.x, f.y, f.r * (0.8 + 0.2 * Math.sin(this._time * 2 + f.ph)));
    }
  }

  dispose() {
    if (this._scene && this._photo) { this._photo.onBeforeRender = null; this._scene.remove(this._photo); this._photo.geometry.dispose(); }
    for (const t of this._textures || []) t.dispose();
    if (this._rippleTex) this._rippleTex.dispose();
    this._u.uMap.value = null; this._u.uMasks.value = null; this._u.uDepth.value = null; this._u.uRipple.value = null;
    this._scene = null;
  }
}
